export interface VehiclePriceRow {
  id: string;
  label: string;
  amount: number;
  isDiscount: boolean;
  /** Last addend before a running total, so it carries the sum line. */
  hasRuleBelow: boolean;
}

export interface VehiclePriceRows {
  /** Rows above the manufacturer-incentives expander. */
  rows: VehiclePriceRow[];
  showIncentives: boolean;
  ruleBelowIncentives: boolean;
  /** Renders below the incentives expander; null when nothing moves the price off MSRP. */
  netRow: VehiclePriceRow | null;
}

type Amount = number | null | undefined;

export interface VehiclePriceInput {
  msrp: Amount;
  vehicleStickerPrice: Amount;
  tradeBoostAmountSaved: Amount;
  creditKarmaAmountSaved: Amount;
  employeeDiscountAmount: Amount;
  priceReductions: Amount;
  totalIncentives: Amount;
  sellsPreOwnedVehicles: boolean;
}

/**
 * Used cars: the no-discounts design collapses the vehicle price to one net "Vehicle
 * price" row; the max design itemizes instead — gross "List price" followed by one green
 * row per discount, with no intermediate net subtotal before Sales tax.
 *
 * Discount order follows the max frame (Bundle & Save, Price reductions, Employee
 * discount).
 */
export const deriveVehiclePriceRows = ({
  msrp,
  vehicleStickerPrice,
  tradeBoostAmountSaved,
  creditKarmaAmountSaved,
  employeeDiscountAmount,
  priceReductions,
  totalIncentives,
  sellsPreOwnedVehicles
}: VehiclePriceInput): VehiclePriceRows => {
  const isNewCar = sellsPreOwnedVehicles && (msrp ?? 0) > 0;
  const sticker = vehicleStickerPrice ?? 0;
  /* Gates the running totals only: a partial payload is no reason to drop the incentives
     disclosure, so that expander still renders without one. */
  const hasListedPrice = sticker > 0;
  const incentives = totalIncentives ?? 0;
  const showIncentives = isNewCar && incentives > 0;

  const discounts = [
    { id: 'bundle', label: 'Bundle & Save discount', amount: tradeBoostAmountSaved ?? 0 },
    { id: 'price-reductions', label: 'Price reductions', amount: priceReductions ?? 0 },
    { id: 'employee', label: 'Employee discount', amount: employeeDiscountAmount ?? 0 },
    { id: 'credit-karma', label: 'Credit Karma savings', amount: creditKarmaAmountSaved ?? 0 }
  ]
    .filter(row => row.amount > 0)
    .map(row => ({ ...row, isDiscount: true, hasRuleBelow: false }));

  const discountTotal = discounts.reduce((total, row) => total + row.amount, 0);
  const afterDiscounts = sticker - discountTotal;

  if (isNewCar) {
    /* Not the API's `carvanaDiscount`: that has no above-MSRP direction, and can drift out
       of step with the "After …" row derived from these same two prices. */
    const msrpGap = hasListedPrice ? (msrp ?? 0) - sticker : 0;
    const isMarketAdjustment = msrpGap < 0;
    const adjustment =
      msrpGap > 0
        ? {
            id: 'customer',
            label: 'Customer discount',
            amount: msrpGap,
            isDiscount: true,
            hasRuleBelow: false
          }
        : isMarketAdjustment
          ? {
              id: 'market-adjustment',
              label: 'Market adjustment',
              amount: -msrpGap,
              isDiscount: false,
              hasRuleBelow: false
            }
          : null;
    const movesOffMsrp = adjustment !== null || discounts.length > 0;
    // Without incentives the net row below the expander already is the listed price.
    const showAfterRow = hasListedPrice && showIncentives && movesOffMsrp;
    const netRow =
      hasListedPrice && (movesOffMsrp || showIncentives)
        ? {
            id: 'net',
            label: 'Vehicle price',
            amount: afterDiscounts - incentives,
            isDiscount: false,
            hasRuleBelow: false
          }
        : null;

    const addends = [
      { id: 'gross', label: 'MSRP', amount: msrp ?? 0, isDiscount: false, hasRuleBelow: false },
      ...(adjustment ? [adjustment] : []),
      ...discounts
    ];
    /* A sum line hangs off whichever element sits directly above a running total, so MSRP
       alone never carries one. */
    const lastAddend = movesOffMsrp ? addends[addends.length - 1] : null;
    const ruleBelowLastAddend = showAfterRow || (netRow !== null && !showIncentives);

    return {
      rows: [
        ...addends.map(row =>
          row === lastAddend && ruleBelowLastAddend ? { ...row, hasRuleBelow: true } : row
        ),
        ...(showAfterRow
          ? [
              {
                id: 'after',
                label: isMarketAdjustment ? 'After adjustment' : 'After discount',
                amount: afterDiscounts,
                isDiscount: false,
                hasRuleBelow: false
              }
            ]
          : [])
      ],
      showIncentives,
      ruleBelowIncentives: showIncentives && netRow !== null,
      netRow
    };
  }

  if (!discounts.length) {
    return {
      rows: [
        {
          id: 'net',
          label: 'Vehicle price',
          amount: sticker - incentives,
          isDiscount: false,
          hasRuleBelow: false
        }
      ],
      showIncentives,
      ruleBelowIncentives: false,
      netRow: null
    };
  }

  return {
    rows: [
      {
        id: 'gross',
        label: 'List price',
        amount: sticker,
        isDiscount: false,
        hasRuleBelow: false
      },
      ...discounts
    ],
    showIncentives,
    ruleBelowIncentives: false,
    netRow: null
  };
};

export default deriveVehiclePriceRows;
